export const HR_PAYROLL_RECORD_ROUTE_ROOT = "/app/hr/records";

export const HR_PAYROLL_RECORD_DOMAINS = Object.freeze([
  "recruitment",
  "forms",
  "automation",
  "workflow-instance",
  "people-operations-integration",
  "onboarding",
  "credentials",
  "workforce-time",
  "workforce-schedules",
  "substitute-coverage",
  "workforce-workload",
  "organisation",
  "compensation",
  "employee-finance",
  "employee-service",
  "offboarding",
  "performance",
  "learning-development",
  "payroll-compliance",
  "workforce-analytics",
  "leave-policy",
  "leave-governance",
  "attendance-evidence",
  "attendance-punch",
  "attendance-sync-batch",
  "biometric",
  "payroll-delivery",
  "payroll-result",
  "payroll-operation",
  "employee-governance",
  "workforce-roster-governance",
  "academic-workload-planning",
]);

// Canonical record routes always render inside the HR & Payroll product shell, even when the
// record's deterministic "open in workspace" destination belongs to another product surface.
// Keep every domain explicit here so adding a new detail contract cannot silently fall through to
// People based on its spelling.
//
// Forms and Automation records intentionally retain a People shell context while their detail
// contracts return users to /app/forms and /app/automation. Caller-owned `scope=self` records keep
// their subject area here; their detail contracts remain responsible for returning to /app/me.
export const HR_PAYROLL_RECORD_DOMAIN_AREAS = Object.freeze({
  recruitment: "people",
  forms: "people",
  automation: "people",
  "workflow-instance": "people",
  "people-operations-integration": "people",
  onboarding: "people",
  credentials: "people",
  "workforce-time": "workforce",
  "workforce-schedules": "workforce",
  "substitute-coverage": "workforce",
  "workforce-workload": "workforce",
  organisation: "workforce",
  compensation: "pay",
  "employee-finance": "pay",
  "employee-service": "people",
  offboarding: "people",
  performance: "talent",
  "learning-development": "talent",
  "payroll-compliance": "pay",
  "workforce-analytics": "insights",
  "leave-policy": "workforce",
  "leave-governance": "workforce",
  "attendance-evidence": "workforce",
  "attendance-punch": "workforce",
  "attendance-sync-batch": "workforce",
  biometric: "workforce",
  "payroll-delivery": "pay",
  "payroll-result": "pay",
  "payroll-operation": "pay",
  "employee-governance": "people",
  "workforce-roster-governance": "workforce",
  "academic-workload-planning": "workforce",
});

const RECORD_DOMAIN_SET = new Set(HR_PAYROLL_RECORD_DOMAINS);

// Broad route-entry guard only. The detail page still evaluates the exact domain/kind permission
// before issuing a request, and every API remains the final authority for tenant and record scope.
// Keep this grounded in the registered domain contracts; it must not become a generic HR bypass.
export const HR_PAYROLL_RECORD_ROUTE_ENTRY_PERMISSIONS = Object.freeze([
  "recruitment:read",
  "forms:read",
  "automation:read",
  "hr_onboarding:read",
  "credentials:read",
  "workforce_time:read",
  "workforce_scheduling:read",
  "substitute_coverage:read",
  "workforce_workload:read",
  "organization:read",
  "compensation:read",
  "employee_finance:read",
  "employee_service:read",
  "employee_service:configure",
  "employee_relations:read",
  "offboarding:read",
  "performance:read",
  "learning:read",
  "statutory:read",
  "workforce_analytics:read",
  "workforce_analytics:export",
  "workforce_analytics:configure",
  "workforce_analytics:approve",
  "leave:read",
  "leave:approve",
  "biometric:read",
  "payroll:read",
  "hr:read",
]);

export function isHrPayrollRecordDomain(value) {
  return RECORD_DOMAIN_SET.has(String(value || ""));
}

function hrPayrollRecordRouteDomain(pathname) {
  const path = String(pathname || "").split("?")[0];
  const match = path.match(/^\/app\/hr\/records\/([^/]+)\//);
  if (!match) return null;

  try {
    const domain = decodeURIComponent(match[1]).toLowerCase();
    return isHrPayrollRecordDomain(domain) ? domain : null;
  } catch {
    return null;
  }
}

export function isHrPayrollRecordPath(pathname) {
  return Boolean(hrPayrollRecordRouteDomain(pathname));
}

export function hrPayrollRecordArea(pathname) {
  const domain = hrPayrollRecordRouteDomain(pathname);
  return domain ? HR_PAYROLL_RECORD_DOMAIN_AREAS[domain] : null;
}

export function hrPayrollRecordRoutePrefix(domain) {
  if (!isHrPayrollRecordDomain(domain)) {
    throw new Error(`Unknown HR/Payroll record domain: ${domain}`);
  }
  return `${HR_PAYROLL_RECORD_ROUTE_ROOT}/${encodeURIComponent(domain)}/`;
}

export function hrPayrollRecordPath(domain, recordId, options = {}) {
  const id = String(recordId || "").trim();
  if (!id) throw new Error("A record ID is required.");
  const query = new URLSearchParams();
  if (options.kind) query.set("kind", String(options.kind));
  if (options.scope) query.set("scope", String(options.scope));
  if (options.asOf) query.set("as_of", String(options.asOf));
  const suffix = query.toString();
  return `${hrPayrollRecordRoutePrefix(domain)}${encodeURIComponent(id)}${suffix ? `?${suffix}` : ""}`;
}

const PAYROLL_RESULT_SEPARATOR = "::";
const LEARNING_ASSIGNMENT_BATCH_SEPARATOR = "::";
const RELATED_RECORD_SEPARATOR = "::";

const relatedRecordIdentity = (ownerId, recordId, label) => {
  const owner = String(ownerId || "").trim();
  const record = String(recordId || "").trim();
  if (!owner || !record) {
    throw new Error(`A ${label} owner ID and record ID are required.`);
  }
  if (owner.includes(RELATED_RECORD_SEPARATOR) || record.includes(RELATED_RECORD_SEPARATOR)) {
    throw new Error(`${label} identities cannot contain the reserved separator.`);
  }
  return `${owner}${RELATED_RECORD_SEPARATOR}${record}`;
};

const parseRelatedRecordIdentity = (value) => {
  const identity = String(value || "");
  const separatorAt = identity.indexOf(RELATED_RECORD_SEPARATOR);
  if (
    separatorAt <= 0
    || separatorAt !== identity.lastIndexOf(RELATED_RECORD_SEPARATOR)
    || separatorAt >= identity.length - RELATED_RECORD_SEPARATOR.length
  ) return null;
  return {
    ownerId: identity.slice(0, separatorAt),
    recordId: identity.slice(separatorAt + RELATED_RECORD_SEPARATOR.length),
  };
};

export function payrollResultRecordIdentity(runId, employeeId) {
  const run = String(runId || "").trim();
  const employee = String(employeeId || "").trim();
  if (!run || !employee) {
    throw new Error("A payroll run ID and employee ID are required.");
  }
  if (run.includes(PAYROLL_RESULT_SEPARATOR) || employee.includes(PAYROLL_RESULT_SEPARATOR)) {
    throw new Error("Payroll result identities cannot contain the reserved separator.");
  }
  return `${run}${PAYROLL_RESULT_SEPARATOR}${employee}`;
}

export function parsePayrollResultRecordIdentity(value) {
  const identity = String(value || "");
  const separatorAt = identity.indexOf(PAYROLL_RESULT_SEPARATOR);
  if (
    separatorAt <= 0
    || separatorAt !== identity.lastIndexOf(PAYROLL_RESULT_SEPARATOR)
    || separatorAt >= identity.length - PAYROLL_RESULT_SEPARATOR.length
  ) return null;
  return {
    runId: identity.slice(0, separatorAt),
    employeeId: identity.slice(separatorAt + PAYROLL_RESULT_SEPARATOR.length),
  };
}

export function payrollResultRecordPath(runId, employeeId) {
  return hrPayrollRecordPath(
    "payroll-result",
    payrollResultRecordIdentity(runId, employeeId),
  );
}

export function learningAssignmentBatchRecordIdentity(requirementId, batchId) {
  const requirement = String(requirementId || "").trim();
  const batch = String(batchId || "").trim();
  if (!requirement || !batch) {
    throw new Error("A learning requirement ID and assignment batch ID are required.");
  }
  if (
    requirement.includes(LEARNING_ASSIGNMENT_BATCH_SEPARATOR)
    || batch.includes(LEARNING_ASSIGNMENT_BATCH_SEPARATOR)
  ) {
    throw new Error("Learning assignment batch identities cannot contain the reserved separator.");
  }
  return `${requirement}${LEARNING_ASSIGNMENT_BATCH_SEPARATOR}${batch}`;
}

export function parseLearningAssignmentBatchRecordIdentity(value) {
  const identity = String(value || "");
  const separatorAt = identity.indexOf(LEARNING_ASSIGNMENT_BATCH_SEPARATOR);
  if (
    separatorAt <= 0
    || separatorAt !== identity.lastIndexOf(LEARNING_ASSIGNMENT_BATCH_SEPARATOR)
    || separatorAt >= identity.length - LEARNING_ASSIGNMENT_BATCH_SEPARATOR.length
  ) return null;
  return {
    requirementId: identity.slice(0, separatorAt),
    batchId: identity.slice(separatorAt + LEARNING_ASSIGNMENT_BATCH_SEPARATOR.length),
  };
}

export function learningAssignmentBatchRecordPath(requirementId, batchId) {
  return hrPayrollRecordPath(
    "learning-development",
    learningAssignmentBatchRecordIdentity(requirementId, batchId),
    { kind: "assignment_batch" },
  );
}

export function formResponseRecordIdentity(formId, responseId) {
  return relatedRecordIdentity(formId, responseId, "form response");
}

export function parseFormResponseRecordIdentity(value) {
  const parsed = parseRelatedRecordIdentity(value);
  return parsed ? { formId: parsed.ownerId, responseId: parsed.recordId } : null;
}

export function formResponseRecordPath(formId, responseId) {
  return hrPayrollRecordPath(
    "forms",
    formResponseRecordIdentity(formId, responseId),
    { kind: "response" },
  );
}

export function formDefinitionVersionRecordIdentity(formId, versionId) {
  return relatedRecordIdentity(formId, versionId, "form definition version");
}

export function parseFormDefinitionVersionRecordIdentity(value) {
  const parsed = parseRelatedRecordIdentity(value);
  return parsed ? { formId: parsed.ownerId, versionId: parsed.recordId } : null;
}

export function formDefinitionVersionRecordPath(formId, versionId) {
  return hrPayrollRecordPath(
    "forms",
    formDefinitionVersionRecordIdentity(formId, versionId),
    { kind: "definition_version" },
  );
}

export function automationRuleVersionRecordIdentity(ruleId, versionId) {
  return relatedRecordIdentity(ruleId, versionId, "automation rule version");
}

export function parseAutomationRuleVersionRecordIdentity(value) {
  const parsed = parseRelatedRecordIdentity(value);
  return parsed ? { ruleId: parsed.ownerId, versionId: parsed.recordId } : null;
}

export function automationRuleVersionRecordPath(ruleId, versionId) {
  return hrPayrollRecordPath(
    "automation",
    automationRuleVersionRecordIdentity(ruleId, versionId),
    { kind: "rule_version" },
  );
}

export function automationRunStepRecordIdentity(runId, stepId) {
  return relatedRecordIdentity(runId, stepId, "automation run step");
}

export function parseAutomationRunStepRecordIdentity(value) {
  const parsed = parseRelatedRecordIdentity(value);
  return parsed ? { runId: parsed.ownerId, stepId: parsed.recordId } : null;
}

export function automationRunStepRecordPath(runId, stepId) {
  return hrPayrollRecordPath(
    "automation",
    automationRunStepRecordIdentity(runId, stepId),
    { kind: "run_step" },
  );
}

export const workforceRecordDomain = (domain) => ({
  time: "workforce-time",
  schedules: "workforce-schedules",
  coverage: "substitute-coverage",
  workload: "workforce-workload",
}[domain] || "");
